import React, { Component, FormEvent } from 'react';
import Editor from '@monaco-editor/react';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { requireNonUndefined } from '../../../util/Assert';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import { ActionState } from '../../../redux/reducers/ActionReducer';
import { changeAction, changeActionIsUpdate, clearAction } from '../../../redux/actions/ActionActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { MicrosoftTeamsJsonModel } from '../../../domain/collection/rule/action/ActionDomains';
import FormicaInput from '../../custom/FormicaInput';
import CustomPasswordInput from '../../custom/CustomPasswordInput';
import LanguageUtil from '../../../util/LanguageUtil';
import ComponentName from '../../../constant/ComponentName';

class ActionMicrosoftTeamsEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      action: { action },
      component: { module },
      user,
    } = this.props;
    const { loading } = this.state;
    const isKyc = module.includes('kyc');

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'grid grid-cols-8 w-full !max-h-[calc(100vh-15rem)] overflow-y-scroll pb-8'}>
        <div className={'col-span-2 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-5'}>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'pages.action.teams.title', defaultMessage: 'INTEGRATION CONFIGURATION' })}
            </span>
            <span className={'flex bg-[#F7F8FA] rounded-[4px] w-full justify-between gap-x-1 px-1 py-1 h-[36px]'}>
              <div
                onClick={() => this.onSelectType('transaction')}
                className={`${action.type == 'transaction' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
              >
                {LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
              </div>
              {isKyc && user.activeRiskType == 'compliance' && (
                <div
                  onClick={() => this.onSelectType('kyc')}
                  className={`${action.type == 'kyc' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
                >
                  {LanguageUtil.get({ id: 'common.label.kyc', defaultMessage: 'KYC' })}
                </div>
              )}
            </span>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
              <FormicaSearchSelectBox
                options={this.getTransactionOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={action.transaction}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
                data-testid={`${ComponentName.ACTION}_channel_select`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                type={'number'}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTimeout(e)}
                value={action.timeout}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeout', defaultMessage: 'Timeout (ms)' })}
                data-testid={`${ComponentName.ACTION}_timeout_input`}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'common.label.teamsConfiguration', defaultMessage: 'MICROSOFT TEAMS CONFIGURATION' })}
            </span>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.tenant', defaultMessage: 'Tenant' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTenant(e)}
                value={(action as MicrosoftTeamsJsonModel).tenant}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.tenant', defaultMessage: 'Tenant' })}
                data-testid={`${ComponentName.ACTION}_tenant_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeChannel(e)}
                value={(action as MicrosoftTeamsJsonModel).channel}
                placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
                data-testid={`${ComponentName.ACTION}_channel_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.token', defaultMessage: 'Token' })} />
              <CustomPasswordInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeToken(e)}
                value={(action as MicrosoftTeamsJsonModel).token}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.token', defaultMessage: 'Token' })}
                data-testid={`${ComponentName.ACTION}_token_input`}
              />
            </div>
          </div>
        </div>
        <div className={'col-span-6 flex flex-col w-full h-full px-5 py-5 gap-y-2'}>
          <span className={'text-primary font-bold text-md'}>{LanguageUtil.get({ id: 'common.label.messages', defaultMessage: 'MESSAGES' })}</span>
          <Editor
            options={{ readOnly: !RoleHelper.canEdit('Action') }}
            className={'!border-[2px] rounded-[20px] py-4'}
            value={(action as MicrosoftTeamsJsonModel).message}
            onChange={(e: string | undefined) => this.onChangeMessage(e)}
            language={'plaintext'}
            data-testid={`${ComponentName.ACTION}_editor_input`}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      this.setState({ transactions, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.computation?.stop();
  };

  private getTransactionOptions() {
    const {
      action: { action },
    } = this.props;
    if (action.type == 'kyc') {
      return [
        {
          label: LanguageUtil.get({ id: 'common.label.individual', defaultMessage: 'Individual' }),
          value: 'individual',
        },
        {
          label: LanguageUtil.get({ id: 'common.label.entity', defaultMessage: 'Entity' }),
          value: 'entity',
        },
      ];
    } else {
      return this.state.transactions.map((transaction) => {
        return {
          label: transaction.name,
          value: transaction.name,
        };
      });
    }
  }

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.transaction = e.value;
    changeAction(action);
  }

  private onChangeTimeout(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.timeout = parseInt(e.currentTarget.value);
    changeAction(action);
  }

  private onChangeChannel(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as MicrosoftTeamsJsonModel).channel = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeTenant(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as MicrosoftTeamsJsonModel).tenant = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeToken(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as MicrosoftTeamsJsonModel).token = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeMessage(e: string | undefined) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as MicrosoftTeamsJsonModel).message = e || '';
    changeAction(action);
  }

  private onSelectType(type: string) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.type = type;
    changeAction(action);
  }
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  transactions: TransactionJsonModel[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
  component: state.component,
  user: state.auth.user,
});
const mapDispatchToProps = {
  changeActionIsUpdate,
  changeAction,
  clearAction,
  enterComponent,
  leaveComponent,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionMicrosoftTeamsEditorComponent));
